/**
 * One report — spec §M, FR-M5's *"all reports export CSV"*. T-3.10, T-6.7;
 * redrawn on the list shell in Wave 15A (C14 §10.6 item 2, ADR-0033).
 *
 * **One screen for all seven.** Every report comes back with the same three
 * things — headline figures, one table, and its caveats — so a per-report
 * screen would be seven renderers of one payload and seven chances for the
 * table on screen to disagree with the file that was exported.
 *
 * The table it renders is literally `payload.table`, which is literally what
 * the CSV contains. That is the property: *what you see is what you export*.
 * The Excel export (Wave 15) writes the same columns under the same headings,
 * narrowed by the search box — the CSV is always the whole report.
 *
 * ## The filter bar only offers what the report takes
 *
 * The service **refuses** a filter a report does not accept rather than
 * ignoring it (`app/domain/reports.py: ACCEPTS`), because a filter that
 * silently does nothing answers a question nobody asked and presents it as the
 * answer to the one they did. So `FILTERS` hides the control instead of the
 * screen sending something that will 400.
 *
 * ## Caveats are rendered, always
 *
 * A report that cannot answer part of itself says so — FR-G8's manual
 * reconciliation has no field behind it, and revenue cannot attribute a partial
 * credit note to a service. Those are printed with the table rather than
 * resolving to a zero a firm would act on.
 */

import { Alert, Button, Input } from '@fiveox/ui'
import { Download } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  useListTable,
} from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type ReportFilters,
  type ReportKey,
  type ReportPayload,
  accepts,
  downloadCsv,
  generatedLabel,
  headlines,
  reports,
} from '../../lib/api/reports'

const KEYS: ReportKey[] = [
  'compliance',
  'receivables',
  'collections',
  'productivity',
  'pipeline',
  'revenue',
  'sales',
]

function isKey(value: string | undefined): value is ReportKey {
  return !!value && (KEYS as string[]).includes(value)
}

/** One line of `payload.table`, keyed by column position so the kit can sort
 *  it: `{ id: '3', c0: 'Kulkarni', c1: '12,000.00' }`. */
export type ReportRow = { id: string } & Record<string, string>

export function reportRows(payload: ReportPayload | null): ReportRow[] {
  if (!payload) return []
  return payload.table.rows.map((row, index) => {
    const out: ReportRow = { id: String(index) }
    row.forEach((cell, cellIndex) => {
      out[`c${cellIndex}`] = cell
    })
    return out
  })
}

export function reportColumns(payload: ReportPayload | null): ListColumn<ReportRow>[] {
  if (!payload) return []
  return payload.table.columns.map((label, index) => ({
    key: `c${index}`,
    label,
    // The first column names the row; the rest are figures, and at 120 each a
    // seven-column report scrolled sideways beside the rail at 1280.
    width: index === 0 ? 120 : 100,
    sortField: `c${index}`,
    required: index === 0,
    render: (row: ReportRow) => (
      <span className={index === 0 ? 'block max-w-[12rem] truncate sm:max-w-none' : 'whitespace-nowrap'}>
        {row[`c${index}`]}
      </span>
    ),
    exportValue: (row: ReportRow) => row[`c${index}`] ?? '',
  }))
}

export function Report() {
  const { key } = useParams<{ key: string }>()
  const [params, setParams] = useSearchParams()
  const [payload, setPayload] = useState<ReportPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const filters: ReportFilters = {
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    period: params.get('period') ?? undefined,
  }

  const load = useCallback(async () => {
    if (!isKey(key)) {
      setError('No such report.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const body = await reports.open(key, filters)
      setPayload(body.data)
    } catch (err) {
      setPayload(null)
      setError(err instanceof ApiError ? err.message : 'That report could not be read.')
    } finally {
      setLoading(false)
    }
    // The search params are the filters; re-read when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, params.toString()])

  useEffect(() => {
    void load()
  }, [load])

  const exportCsv = async () => {
    if (!isKey(key)) return
    setExporting(true)
    setError(null)
    try {
      // Fetched with the bearer and the org header — a plain link would
      // download a 401 named like a report.
      const blob = await downloadCsv(key, filters)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `prodesk-${key}.csv`
      link.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That report could not be exported.')
    } finally {
      setExporting(false)
    }
  }

  const setFilter = (name: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(name, value)
    else next.delete(name)
    setParams(next, { replace: true })
  }

  const rows = useMemo(() => reportRows(payload), [payload])
  const columns = useMemo(() => reportColumns(payload), [payload])
  const list = useListTable(rows, {
    columns,
    search: (row) => Object.values(row),
  })

  const showDates = isKey(key) && accepts(key, 'from')
  const showPeriod = isKey(key) && accepts(key, 'period')
  const anyFilter = Boolean(filters.from || filters.to || filters.period)

  return (
    <ListPage
      back={{ to: '/reports', label: 'All reports' }}
      title={payload?.title ?? 'Report'}
      description={
        payload
          ? `Generated ${generatedLabel(payload.generated_at)}. The CSV is the whole report; Excel is what you have searched to.`
          : undefined
      }
      primary={
        <Button
          onClick={() => void exportCsv()}
          disabled={!payload || exporting}
          title={payload ? undefined : 'The report has to open before it can be exported.'}
        >
          <Download size={16} aria-hidden />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      }
      // Only the first read hides the toolbar: a re-read on a changed date
      // must not take the date field away from under the cursor.
      loading={loading && !payload}
      error={error}
      isEmpty={payload === null}
      empty={{
        title: 'This report is not open',
        description: 'Go back to Reports and choose one that is open to you.',
      }}
      toolbar={
        <ListToolbar
          list={list}
          placeholder="Search this report"
          actions={
            <ExportButton rows={list.sorted} columns={columns} filename={`prodesk-${key ?? 'report'}`} label="Excel" />
          }
        >
          {/* The kit's Input is a block; each sits in a box of its own width
              so the range reads as one line. */}
          {showDates && (
            <div className="flex items-center gap-2">
              <div className="w-40">
                <Input
                  size="sm"
                  type="date"
                  aria-label="From"
                  value={filters.from ?? ''}
                  onChange={(event) => setFilter('from', event.target.value)}
                />
              </div>
              <span className="text-muted-foreground text-xs">to</span>
              <div className="w-40">
                <Input
                  size="sm"
                  type="date"
                  aria-label="To"
                  value={filters.to ?? ''}
                  onChange={(event) => setFilter('to', event.target.value)}
                />
              </div>
            </div>
          )}
          {showPeriod && (
            <div className="w-32">
              <Input
                size="sm"
                aria-label="Period"
                value={filters.period ?? ''}
                placeholder="MM-YYYY"
                onChange={(event) => setFilter('period', event.target.value)}
              />
            </div>
          )}
        </ListToolbar>
      }
    >
      {payload && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {headlines(payload).map(([label, value]) => (
              <div
                key={label}
                data-headline={label}
                className="border-border min-w-0 rounded-2xl border bg-card p-4 shadow-sm"
              >
                <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {label}
                </div>
                <div className="text-foreground mt-1 truncate text-2xl font-semibold tabular-nums">
                  {value}
                </div>
              </div>
            ))}
          </div>

          <ListTable
            list={list}
            rowKey={(row) => row.id}
            loading={loading}
            empty={
              <span>
                Nothing in this range.
                {anyFilter && (
                  <Button
                    variant="link"
                    className="ml-1"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                  >
                    Clear the dates
                  </Button>
                )}
              </span>
            }
          />

          {payload.caveats.length > 0 && (
            <Alert tone="info">
              <ul className="space-y-1">
                {payload.caveats.map((caveat) => (
                  <li key={caveat}>{caveat}</li>
                ))}
              </ul>
            </Alert>
          )}
        </>
      )}
    </ListPage>
  )
}
